import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  MenuItem,
  Paper,
  Divider,
} from '@mui/material';
import { useAuthStore } from '../stores/authStore';

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'info' | 'success' | 'error';
}

const SendModeTest: React.FC = () => {
  const { token } = useAuthStore();
  const [phoneNumber, setPhoneNumber] = useState('+353851610136');
  const [customerName, setCustomerName] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [scenario, setScenario] = useState('service_reminder');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<{ success: boolean; data?: any; error?: string } | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [{ timestamp, message, type }, ...prev].slice(0, 10));
  };

  useEffect(() => {
    addLog('Test page loaded. Ready to send SMS!', 'info');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResponse(null);

    try {
      addLog(`Sending SMS to ${phoneNumber}...`);

      if (!token) {
        throw new Error('Not authenticated');
      }

      const res = await fetch('/v1/simulator/start-real-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_name: customerName,
          phone_number: phoneNumber,
          scenario_id: scenario,
          vehicle_info: vehicleInfo || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }

      addLog('✅ SMS sent successfully!', 'success');
      addLog(`📱 Request ID: ${data.sendmode_request_id}`, 'info');
      addLog(`💬 Message: "${data.message_sent}"`, 'info');
      addLog(`👤 Customer: ${data.customer_id}`, 'info');

      setResponse({ success: true, data });
    } catch (error: any) {
      addLog(`❌ Error sending SMS: ${error.message}`, 'error');
      setResponse({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        📱 SendMode SMS Test Page
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Test two-way SMS functionality with SendMode Engage API
      </Typography>

      <Box sx={{ my: 3 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>How to Use:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            1. Enter a valid phone number in E.164 format (e.g., +353871234567)
            <br />
            2. Fill in customer details and choose a scenario
            <br />
            3. Click "Send SMS" to send a test message
            <br />
            4. Reply to the SMS from your phone
            <br />
            5. Check the logs to see the inbound webhook received your reply
          </Typography>
        </Alert>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Send Test SMS
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Phone Number (E.164 format)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+353871234567"
              required
              sx={{ mb: 2 }}
              inputProps={{
                pattern: '^\\+[1-9]\\d{1,14}$',
              }}
            />

            <TextField
              fullWidth
              label="Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="John Doe"
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Vehicle Info"
              value={vehicleInfo}
              onChange={(e) => setVehicleInfo(e.target.value)}
              placeholder="2021 BMW 3 Series"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              select
              label="Scenario"
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              sx={{ mb: 3 }}
            >
              <MenuItem value="service_reminder">Service Reminder</MenuItem>
              <MenuItem value="trade_in">Trade-In Offer</MenuItem>
              <MenuItem value="test_drive">Test Drive Invitation</MenuItem>
            </TextField>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                },
              }}
            >
              {loading ? 'Sending...' : 'Send SMS'}
            </Button>
          </form>

          {response && (
            <Box sx={{ mt: 2 }}>
              {response.success ? (
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>✅ SMS Sent Successfully!</strong>
                  </Typography>
                  <Typography variant="body2">Request ID: {response.data?.sendmode_request_id}</Typography>
                  <Typography variant="body2">Customer ID: {response.data?.customer_id}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Next Step:</strong> Reply to the SMS from your phone and check the logs below!
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>❌ Error Sending SMS</strong>
                  </Typography>
                  <Typography variant="body2">{response.error}</Typography>
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {logs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No activity yet. Send an SMS to see logs here.
            </Typography>
          ) : (
            <Box>
              {logs.map((log, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {log.timestamp}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      color: log.type === 'error' ? '#dc3545' : log.type === 'success' ? '#28a745' : '#666',
                    }}
                  >
                    {log.message}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SendModeTest;
